'use client'

import { IMediaItem } from '@/lib/types/media/core'
import { showToast } from '@/lib/utils/toast'
import { Button, Modal } from '@heroui/react'
import { MediaPlacement, MediaType } from '@prisma/client'
import { useCallback, useEffect, useState } from 'react'
import { MediaDropzone } from '../core/MediaDropzone'
import { MediaPreview } from '../core/MediaPreview'

export interface MediaPickerProps {
  placement: MediaPlacement
  type?: MediaType
  ownerId: string
  value?: IMediaItem | null
  onChange?: (media: IMediaItem | null) => void
  aspectRatio?: 'square' | 'video' | 'auto'
  className?: string
}

export function MediaPicker({
  placement,
  type,
  ownerId,
  value,
  onChange,
  aspectRatio = 'video',
  className
}: MediaPickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [items, setItems] = useState<IMediaItem[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const loadItems = useCallback(async () => {
    try {
      setIsLoading(true)
      // Load media items from API
      const params = new URLSearchParams({
        placement,
        ownerId
      })
      if (type) {
        params.append('type', type)
      }
      
      const response = await fetch(`/api/media/list?${params}`)
      if (!response.ok) {
        throw new Error('Failed to fetch media items')
      }
      const data = await response.json()
      const items = data.success ? data.media || [] : []
      setItems(items)
    } catch (error) {
      console.error('Failed to load media items:', error)
      showToast({ title: 'Failed to load media items', color: 'danger', timeout: 5000 })
    } finally {
      setIsLoading(false)
    }
  }, [placement, type, ownerId])

  useEffect(() => {
    if (isOpen) {
      loadItems()
    }
  }, [isOpen, loadItems])

  const handleSelect = useCallback(
    (item: IMediaItem) => {
      onChange?.(item)
      setIsOpen(false)
    },
    [onChange]
  )

  const handleClear = useCallback(() => {
    onChange?.(null)
  }, [onChange])

  return (
    <div className={className}>
      {/* Selected Media Preview */}
      {value ? (
        <div className='space-y-2'>
          <MediaPreview media={value} aspectRatio={aspectRatio} showControls={false} />
          <div className='flex justify-end gap-2'>
            <Button variant='ghost' onPress={handleClear}>
              Clear
            </Button>
            <Button variant='ghost' onPress={() => setIsOpen(true)}>
              Change
            </Button>
          </div>
        </div>
      ) : (
        <Button variant='ghost' onPress={() => setIsOpen(true)} className='w-full p-8'>
          Select Media
        </Button>
      )}

      {/* Media Selection Dialog */}
      <Modal.Backdrop
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) setIsOpen(false)
        }}
      >
        <Modal.Container>
          <Modal.Dialog>
            <Modal.Body>
        <div className='space-y-4'>
          {/* Upload Section */}
          <div className='p-4 bg-gray-50 rounded-lg'>
            <MediaDropzone
              onDrop={async (files) => {
                const file = files[0]
                if (!file) return

                try {
                  // Upload using fetch to /api/upload
                  const formData = new FormData()
                  formData.append('file', file)
                  formData.append('placement', placement)
                  formData.append('userId', ownerId)
                  formData.append('uploadId', `upload-${Date.now()}`)

                  const response = await fetch('/api/upload', {
                    method: 'POST',
                    body: formData
                  })

                  if (!response.ok) {
                    throw new Error('Upload failed')
                  }

                  const result = await response.json()
                  if (!result.success || !result.media) {
                    throw new Error(result.error || 'Upload failed')
                  }

                  handleSelect(result.media)
                  showToast({ title: 'Upload complete', color: 'success', timeout: 3000 })
                } catch (error) {
                  console.error('Upload error:', error)
                  showToast({ title: 'Upload failed', color: 'danger', timeout: 5000 })
                }
              }}
              acceptedTypes={{ 'image/*': [], 'video/*': [] }}
              maxSize={10 * 1024 * 1024} // 10MB
              multiple={false}
            />
          </div>

          {/* Existing Media Grid */}
          <div className='grid grid-cols-3 gap-4'>
            {isLoading ? (
              <div className='col-span-3 text-center py-8 text-gray-500'>Loading...</div>
            ) : items.length === 0 ? (
              <div className='col-span-3 text-center py-8 text-gray-500'>No media items found</div>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  className='focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg'
                >
                  <MediaPreview media={item} aspectRatio={aspectRatio} showControls={false} />
                </button>
              ))
            )}
          </div>
        </div>
            </Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </div>
  )
}
